import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { AppConfigService } from 'src/app/services/app-config/app-config.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from 'src/app/services/auth/auth.service';
import * as alertifyjs from 'alertifyjs';

@Component({
    selector: 'app-app-config',
    templateUrl: './app-config.component.html',
    styleUrls: ['./app-config.component.css']
})
export class AppConfigComponent implements OnInit {
    activeTab: string = 'services'; // 'services', 'simon', 'register', 'carrusel', or 'homeModal'

    // Services tab
    services: any[] = [];
    rootServices: any[] = [];
    servicesLoading: boolean = false;

    // Add service form
    showAddServiceModal: boolean = false;
    addServiceForm: FormGroup;
    addServiceLoading: boolean = false;

    // SIMON tab
    showSimon: boolean = false;
    simonLoading: boolean = false;
    simonToggleLoading: boolean = false;

    // Register tab
    registerEnabled: boolean = false;
    registerMessage: string = '';
    registerLoading: boolean = false;
    registerToggleLoading: boolean = false;
    registerMessageSaving: boolean = false;

    // Carousel tab
    carruselEnabled: boolean = false;
    carruselItems: any[] = [];
    carruselLoading: boolean = false;
    carruselToggleLoading: boolean = false;

    // Add carousel item form
    showAddCarruselItemModal: boolean = false;
    addCarruselItemForm: FormGroup;
    addCarruselItemLoading: boolean = false;

    // Modal Home App tab
    homeModalEnabled: boolean = false;
    homeModalHtml: string = '';
    homeModalLoading: boolean = false;
    homeModalToggleLoading: boolean = false;
    homeModalHtmlSaving: boolean = false;

    constructor(
        private appConfigService: AppConfigService,
        private formBuilder: FormBuilder,
        private cdr: ChangeDetectorRef,
        public _auth: AuthService
    ) {
        this.addServiceForm = this.formBuilder.group({
            code: ['', [Validators.required]],
            name: ['', [Validators.required]],
            description: [''],
            order: [1, [Validators.required, Validators.min(1)]],
            parent: ['']
        });

        this.addCarruselItemForm = this.formBuilder.group({
            title: ['', [Validators.required]],
            description: ['', [Validators.required]],
            icon: ['', [Validators.required]],
            order: [1, [Validators.required, Validators.min(1)]],
            action: [''],
            status: [true]
        });
    }

    ngOnInit(): void {
        this.loadServices();
        this.loadShowSimon();
        this.loadRegisterConfig();
        this.loadCarruselConfig();
        this.loadHomeModalConfig();
    }

    /**
     * Load available services
     */
    loadServices = async () => {
        try {
            this.servicesLoading = true;
            const resp = await this.appConfigService.getAvailableServices();

            // Try different possible response structures
            let servicesArray = null;

            if (Array.isArray(resp)) {
                servicesArray = resp;
            } else if (resp?.data && Array.isArray(resp.data)) {
                servicesArray = resp.data;
            } else if (resp?.services && Array.isArray(resp.services)) {
                servicesArray = resp.services;
            } else if (resp?.data?.services && Array.isArray(resp.data.services)) {
                servicesArray = resp.data.services;
            } else if (resp?.result && Array.isArray(resp.result)) {
                servicesArray = resp.result;
            } else {
                console.warn('Could not find services array in response');
                servicesArray = [];
            }

            // Normalize services - convert 'status' field to 'enabled' boolean
            this.services = servicesArray.map((service: any) => {
                // The API uses 'status' field - convert to boolean 'enabled'
                let enabled = false;

                if (service.status !== undefined) {
                    if (typeof service.status === 'string') {
                        enabled = service.status.toLowerCase() === 'active';
                    } else if (typeof service.status === 'boolean') {
                        enabled = service.status;
                    } else if (typeof service.status === 'number') {
                        enabled = service.status === 1;
                    }
                }

                return {
                    ...service,
                    enabled: enabled,
                    children: [] // Initialize children array
                };
            });

            this.buildServiceHierarchy();
            console.log('Services loaded:', this.services.length, 'services');
            console.log('Root services:', this.rootServices.length);

        } catch (error) {
            console.error('Error loading services:', error);
            alertifyjs.error('Error al cargar los servicios');
            this.services = [];
            this.rootServices = [];
        } finally {
            this.servicesLoading = false;
        }
    };

    /**
     * Build service hierarchy tree
     */
    buildServiceHierarchy() {
        const serviceMap = new Map();
        this.rootServices = [];

        // First pass: Map services by code
        this.services.forEach(service => {
            service.children = []; // Reset children
            serviceMap.set(service.code, service);
        });

        // Second pass: Organize into tree
        this.services.forEach(service => {
            if (service.parent && serviceMap.has(service.parent)) {
                const parent = serviceMap.get(service.parent);
                parent.children.push(service);
            } else {
                // If no parent or parent not found, it's a root service
                this.rootServices.push(service);
            }
        });
    }

    /**
     * Toggle service enabled/disabled
     */
    /**
     * Toggle service enabled/disabled
     */
    toggleService = async (service: any, event: any) => {
        if (service.loading) {
            event.preventDefault();
            return;
        }

        const checkbox = event.target;
        const newValue = checkbox.checked;

        await this.updateServiceStatus(service, newValue, checkbox);
    };

    /**
     * Internal method to update service status
     */
    updateServiceStatus = async (service: any, newValue: boolean, checkbox?: any) => {
        try {
            service.loading = true;

            // Use the toggle-status endpoint
            const resp = await this.appConfigService.toggleServiceStatus(service.code, newValue);

            if (resp) {
                service.enabled = newValue;
                service.status = newValue ? 'active' : 'inactive';

                if (checkbox) {
                    // Only show alert if triggered by user interaction
                    alertifyjs.success(newValue ? 'Servicio activado' : 'Servicio desactivado');
                }

                // Check parent status if this is a child service
                if (service.parent) {
                    await this.checkParentStatus(service);
                }
            } else {
                if (checkbox) checkbox.checked = !newValue;
                service.enabled = !newValue;
                alertifyjs.error('Error al actualizar el servicio');
            }
        } catch (error) {
            console.error('Error toggling service:', error);
            alertifyjs.error('Error al actualizar el servicio');
            if (checkbox) checkbox.checked = !newValue;
            service.enabled = !newValue;
        } finally {
            service.loading = false;
        }
    };

    /**
     * Check and update parent status based on children
     */
    checkParentStatus = async (childService: any) => {
        // Find parent service
        const parent = this.services.find(s => s.code === childService.parent);

        if (!parent) return;

        // Get all children of this parent
        const children = this.services.filter(s => s.parent === parent.code);

        // Check if any child is active
        const anyChildActive = children.some(child => child.enabled);

        // Logic:
        // 1. If all children are inactive -> Inactive parent
        // 2. If at least one child is active -> Active parent

        if (anyChildActive && !parent.enabled) {
            // Activate parent
            console.log('Activating parent service:', parent.name);
            await this.updateServiceStatus(parent, true);
        } else if (!anyChildActive && parent.enabled) {
            // Deactivate parent
            console.log('Deactivating parent service:', parent.name);
            await this.updateServiceStatus(parent, false);
        }
    };

    /**
     * Open add service modal
     */
    openAddServiceModal = () => {
        this.showAddServiceModal = true;
        this.addServiceForm.reset({
            code: '',
            name: '',
            description: '',
            order: 1,
            parent: ''
        });
    };

    /**
     * Close add service modal
     */
    closeAddServiceModal = () => {
        this.showAddServiceModal = false;
    };

    /**
     * Submit add service form
     */
    submitAddService = async () => {
        if (this.addServiceForm.valid) {
            try {
                this.addServiceLoading = true;
                const formData = this.addServiceForm.getRawValue();

                const resp = await this.appConfigService.addService(formData);

                if (resp) {
                    alertifyjs.success('Servicio agregado exitosamente');
                    this.closeAddServiceModal();
                    await this.loadServices();
                } else {
                    alertifyjs.error('Error al agregar el servicio');
                }
            } catch (error: any) {
                console.error('Error adding service:', error);
                const errorMsg = error?.error?.msg || error?.error?.message || 'Error al agregar el servicio';
                alertifyjs.error(errorMsg);
            } finally {
                this.addServiceLoading = false;
            }
        } else {
            alertifyjs.error('Por favor complete todos los campos requeridos');
        }
    };

    /**
     * Delete service with confirmation
     */
    /**
     * Delete service with confirmation
     */
    deleteService = async (service: any) => {
        let confirmMessage = `¿Está seguro que desea eliminar el servicio "${service.name}"?`;

        if (service.children && service.children.length > 0) {
            confirmMessage = `¿Está seguro que desea eliminar el servicio "${service.name}"?\n\nADVERTENCIA: Esto también eliminará sus ${service.children.length} sub-servicios asociados.`;
        }

        const confirmed = confirm(confirmMessage);

        if (!confirmed) {
            return;
        }

        try {
            service.deleting = true;

            // If it has children, delete them first
            if (service.children && service.children.length > 0) {
                for (const child of service.children) {
                    try {
                        await this.appConfigService.deleteService(child.code);
                    } catch (error) {
                        console.error(`Error deleting child service ${child.code}:`, error);
                        // Continue deleting other children and parent even if one fails
                    }
                }
            }

            // Delete the parent service
            const resp = await this.appConfigService.deleteService(service.code);

            if (resp) {
                alertifyjs.success('Servicio eliminado exitosamente');
                await this.loadServices();
            } else {
                alertifyjs.error('Error al eliminar el servicio');
            }
        } catch (error: any) {
            console.error('Error deleting service:', error);
            const errorMsg = error?.error?.msg || error?.error?.message || 'Error al eliminar el servicio';
            alertifyjs.error(errorMsg);
        } finally {
            service.deleting = false;
        }
    };

    /**
     * Load SIMON configuration
     */
    loadShowSimon = async () => {
        try {
            this.simonLoading = true;
            const resp = await this.appConfigService.getShowSimon();

            let value: any;

            if (resp?.data?.show_simon !== undefined) {
                value = resp.data.show_simon;
            } else if (resp?.show_simon !== undefined) {
                value = resp.show_simon;
            } else {
                value = false;
            }

            // Convert to boolean explicitly
            if (value === 'false' || value === false || value === 0 || value === '0') {
                this.showSimon = false;
            } else if (value === 'true' || value === true || value === 1 || value === '1') {
                this.showSimon = true;
            } else {
                this.showSimon = false;
            }

            console.log('showSimon set to:', this.showSimon);
            this.cdr.detectChanges();
        } catch (error) {
            console.error('Error loading show simon:', error);
            this.showSimon = false;
            this.cdr.detectChanges();
        } finally {
            this.simonLoading = false;
        }
    };

    /**
     * Toggle SIMON visibility
     */
    onSimonToggleChange = async (event: any) => {
        if (this.simonToggleLoading) {
            event.preventDefault();
            return;
        }

        const checkbox = event.target;
        const newValue = checkbox.checked;

        try {
            this.simonToggleLoading = true;

            const resp = await this.appConfigService.updateShowSimon(newValue);

            if (resp) {
                this.showSimon = newValue;
                alertifyjs.success(newValue ? 'SIMON chat activado en la app' : 'SIMON chat desactivado en la app');
            } else {
                checkbox.checked = !newValue;
                this.showSimon = !newValue;
                alertifyjs.error('Error al actualizar la configuración');
            }
        } catch (error) {
            console.error('Error updating show simon:', error);
            alertifyjs.error('Error al actualizar la configuración');
            checkbox.checked = !newValue;
            this.showSimon = !newValue;
        } finally {
            this.simonToggleLoading = false;
        }
    };

    /**
     * Load Register configuration
     */
    loadRegisterConfig = async () => {
        try {
            this.registerLoading = true;
            const resp = await this.appConfigService.getRegisterConfig();

            let enabled = false;
            let message = '';

            if (resp?.data?.enable !== undefined) {
                enabled = resp.data.enable;
            } else if (resp?.enable !== undefined) {
                enabled = resp.enable;
            }

            if (resp?.data?.text !== undefined) {
                message = resp.data.text;
            } else if (resp?.text !== undefined) {
                message = resp.text;
            }

            // Convert to boolean explicitly
            if (typeof enabled === 'string') {
                enabled = enabled === 'true' || enabled === '1';
            } else if (typeof enabled === 'number') {
                enabled = enabled === 1;
            }

            this.registerEnabled = !!enabled;
            this.registerMessage = message || '';
            console.log('registerEnabled set to:', this.registerEnabled);
            console.log('registerMessage set to:', this.registerMessage);
            this.cdr.detectChanges();
        } catch (error) {
            console.error('Error loading register config:', error);
            this.registerEnabled = false;
            this.registerMessage = '';
            this.cdr.detectChanges();
        } finally {
            this.registerLoading = false;
        }
    };

    /**
     * Toggle Register enable/disable
     */
    onRegisterToggleChange = async (event: any) => {
        if (this.registerToggleLoading) {
            event.preventDefault();
            return;
        }

        const checkbox = event.target;
        const newValue = checkbox.checked;

        try {
            this.registerToggleLoading = true;

            const resp = await this.appConfigService.toggleRegisterStatus(newValue);

            if (resp) {
                this.registerEnabled = newValue;
                alertifyjs.success(newValue ? 'Registro de usuarios activado' : 'Registro de usuarios desactivado');
            } else {
                checkbox.checked = !newValue;
                this.registerEnabled = !newValue;
                alertifyjs.error('Error al actualizar la configuración');
            }
        } catch (error) {
            console.error('Error updating register status:', error);
            alertifyjs.error('Error al actualizar la configuración');
            checkbox.checked = !newValue;
            this.registerEnabled = !newValue;
        } finally {
            this.registerToggleLoading = false;
        }
    };

    /**
     * Save Register message
     */
    saveRegisterMessage = async () => {
        try {
            this.registerMessageSaving = true;

            const resp = await this.appConfigService.updateRegisterMessage(this.registerMessage);

            if (resp) {
                alertifyjs.success('Mensaje actualizado exitosamente');
            } else {
                alertifyjs.error('Error al actualizar el mensaje');
            }
        } catch (error) {
            console.error('Error saving register message:', error);
            alertifyjs.error('Error al actualizar el mensaje');
        } finally {
            this.registerMessageSaving = false;
        }
    };

    /**
     * Load Carousel configuration
     */
    loadCarruselConfig = async () => {
        try {
            this.carruselLoading = true;
            const resp = await this.appConfigService.getCarruselConfig();
            console.log('Carrusel raw response:', resp);

            // Default values
            this.carruselEnabled = false;
            this.carruselItems = [];

            if (resp && resp.data) {
                // Handle Global Status (check multiple possible locations)
                if (resp.data.status !== undefined) {
                    this.carruselEnabled = resp.data.status;
                } else if (resp.data.carrusel_config && resp.data.carrusel_config.status !== undefined) {
                    this.carruselEnabled = resp.data.carrusel_config.status;
                } else if (resp.data.enabled !== undefined) {
                    this.carruselEnabled = resp.data.enabled;
                }

                // Handle Items List (check multiple possible locations)
                if (resp.data.carrusel_config && Array.isArray(resp.data.carrusel_config.list)) {
                    this.carruselItems = resp.data.carrusel_config.list;
                } else if (Array.isArray(resp.data.list)) {
                    this.carruselItems = resp.data.list;
                } else if (Array.isArray(resp.data.items)) {
                    this.carruselItems = resp.data.items;
                }
            }

            console.log('Carrusel config loaded:', {
                enabled: this.carruselEnabled,
                itemsCount: this.carruselItems.length,
                items: this.carruselItems
            });
        } catch (error) {
            console.error('Error loading carrusel config:', error);
            this.carruselEnabled = false;
            this.carruselItems = [];
        } finally {
            this.carruselLoading = false;
        }
    };

    /**
     * Toggle Carousel global status
     */
    onCarruselToggleChange = async (event: any) => {
        if (this.carruselToggleLoading) {
            event.preventDefault();
            return;
        }

        const checkbox = event.target;
        const newValue = checkbox.checked;

        try {
            this.carruselToggleLoading = true;

            const resp = await this.appConfigService.toggleCarruselStatus(newValue);

            if (resp) {
                this.carruselEnabled = newValue;
                alertifyjs.success(newValue ? 'Carrusel activado' : 'Carrusel desactivado');
            } else {
                checkbox.checked = !newValue;
                this.carruselEnabled = !newValue;
                alertifyjs.error('Error al actualizar el carrusel');
            }
        } catch (error) {
            console.error('Error updating carrusel status:', error);
            alertifyjs.error('Error al actualizar el carrusel');
            checkbox.checked = !newValue;
            this.carruselEnabled = !newValue;
        } finally {
            this.carruselToggleLoading = false;
        }
    };

    /**
     * Toggle Carousel Item status
     */
    toggleCarruselItem = async (item: any, event: any) => {
        const checkbox = event.target;
        const newValue = checkbox.checked;

        try {
            // Optimistic update
            item.status = newValue;

            const resp = await this.appConfigService.toggleCarruselItemStatus(item.title, newValue);

            if (resp) {
                alertifyjs.success(newValue ? 'Item activado' : 'Item desactivado');
            } else {
                // Revert on error
                item.status = !newValue;
                checkbox.checked = !newValue;
                alertifyjs.error('Error al actualizar el item');
            }
        } catch (error) {
            console.error('Error toggling carrusel item:', error);
            item.status = !newValue;
            checkbox.checked = !newValue;
            alertifyjs.error('Error al actualizar el item');
        }
    };

    /**
     * Delete Carousel Item
     */
    deleteCarruselItem = async (item: any) => {
        const confirmed = confirm(`¿Está seguro que desea eliminar el item "${item.title}"?`);

        if (!confirmed) {
            return;
        }

        try {
            const resp = await this.appConfigService.deleteCarruselItem(item.title);

            if (resp) {
                alertifyjs.success('Item eliminado exitosamente');
                await this.loadCarruselConfig();
            } else {
                alertifyjs.error('Error al eliminar el item');
            }
        } catch (error: any) {
            console.error('Error deleting carrusel item:', error);
            const errorMsg = error?.error?.msg || error?.error?.message || 'Error al eliminar el item';
            alertifyjs.error(errorMsg);
        }
    };

    /**
     * Open add carousel item modal
     */
    openAddCarruselItemModal = () => {
        this.showAddCarruselItemModal = true;
        this.addCarruselItemForm.reset({
            title: '',
            description: '',
            icon: '',
            order: this.carruselItems.length + 1,
            action: '',
            status: true
        });
    };

    /**
     * Close add carousel item modal
     */
    closeAddCarruselItemModal = () => {
        this.showAddCarruselItemModal = false;
    };

    /**
     * Submit add carousel item form
     */
    submitAddCarruselItem = async () => {
        if (this.addCarruselItemForm.valid) {
            try {
                this.addCarruselItemLoading = true;
                const formData = this.addCarruselItemForm.getRawValue();

                const resp = await this.appConfigService.addCarruselItem(formData);

                if (resp) {
                    alertifyjs.success('Item agregado exitosamente');
                    this.closeAddCarruselItemModal();
                    await this.loadCarruselConfig();
                } else {
                    alertifyjs.error('Error al agregar el item');
                }
            } catch (error: any) {
                console.error('Error adding carrusel item:', error);
                const errorMsg = error?.error?.msg || error?.error?.message || 'Error al agregar el item';
                alertifyjs.error(errorMsg);
            } finally {
                this.addCarruselItemLoading = false;
            }
        } else {
            alertifyjs.error('Por favor complete todos los campos requeridos');
        }
    };

    /**
     * Load Modal Home configuration
     */
    loadHomeModalConfig = async () => {
        try {
            this.homeModalLoading = true;
            const resp = await this.appConfigService.getHomeModalConfig();

            let enabled = false;
            let html = '';

            if (resp?.data?.enabled !== undefined) {
                enabled = resp.data.enabled;
            }

            if (resp?.data?.html !== undefined) {
                html = resp.data.html;
            }

            this.homeModalEnabled = !!enabled;
            this.homeModalHtml = html || '';
            console.log('homeModalEnabled set to:', this.homeModalEnabled);
            console.log('homeModalHtml set to:', this.homeModalHtml);
            this.cdr.detectChanges();
        } catch (error) {
            console.error('Error loading home modal config:', error);
            this.homeModalEnabled = false;
            this.homeModalHtml = '';
            this.cdr.detectChanges();
        } finally {
            this.homeModalLoading = false;
        }
    };

    /**
     * Toggle Modal Home enable/disable
     */
    onHomeModalToggleChange = async (event: any) => {
        if (this.homeModalToggleLoading) {
            event.preventDefault();
            return;
        }

        const checkbox = event.target;
        const newValue = checkbox.checked;

        try {
            this.homeModalToggleLoading = true;

            const resp = await this.appConfigService.toggleHomeModalStatus(newValue);

            if (resp) {
                this.homeModalEnabled = newValue;
                alertifyjs.success(newValue ? 'Modal home activado' : 'Modal home desactivado');
            } else {
                checkbox.checked = !newValue;
                this.homeModalEnabled = !newValue;
                alertifyjs.error('Error al actualizar la configuración');
            }
        } catch (error) {
            console.error('Error updating home modal status:', error);
            alertifyjs.error('Error al actualizar la configuración');
            checkbox.checked = !newValue;
            this.homeModalEnabled = !newValue;
        } finally {
            this.homeModalToggleLoading = false;
        }
    };

    /**
     * Save Modal Home HTML
     */
    saveHomeModalHtml = async () => {
        try {
            this.homeModalHtmlSaving = true;

            const resp = await this.appConfigService.updateHomeModalHtml(this.homeModalHtml);

            if (resp) {
                alertifyjs.success('HTML del modal actualizado exitosamente');
            } else {
                alertifyjs.error('Error al actualizar el HTML');
            }
        } catch (error) {
            console.error('Error saving home modal html:', error);
            alertifyjs.error('Error al actualizar el HTML');
        } finally {
            this.homeModalHtmlSaving = false;
        }
    };

    /**
     * Change active tab
     */
    changeTab(tab: string) {
        this.activeTab = tab;
    }
}
